import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Mic, MicOff, Camera, AlertTriangle, Loader2, Check, Siren } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { AudioRecorder } from "@/utils/audioRecorder";
import { cn } from "@/lib/utils";

type Severity = "first_aid" | "medical_treatment" | "fatality";
type AreaStatus = "still_unsafe" | "secured";

const SEVERITY_OPTIONS: { value: Severity; label: string; icon: string; color: string }[] = [
  { value: "first_aid", label: "P3K / Minor", icon: "🩹", color: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  { value: "medical_treatment", label: "Butuh Medis", icon: "🏥", color: "bg-orange-100 text-orange-800 border-orange-300" },
  { value: "fatality", label: "Serius/Fatal", icon: "☠️", color: "bg-red-100 text-red-800 border-red-300" },
];

const AREA_OPTIONS: { value: AreaStatus; label: string; icon: string; color: string }[] = [
  { value: "still_unsafe", label: "BERBAHAYA", icon: "⚠️", color: "bg-destructive text-destructive-foreground" },
  { value: "secured", label: "AMAN", icon: "✅", color: "bg-success text-success-foreground" },
];

const QuickIncident = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [severity, setSeverity] = useState<Severity | null>(null);
  const [areaStatus, setAreaStatus] = useState<AreaStatus | null>(null);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [extractedContext, setExtractedContext] = useState<any>(null);
  
  const [workerId, setWorkerId] = useState<string | null>(null);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [siteId, setSiteId] = useState<string | null>(null);
  
  const audioRecorderRef = useRef<AudioRecorder | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/signup");
      return;
    }

    const { data: profile } = await supabase
      .from("worker_profiles")
      .select("id, company_id, site_id")
      .eq("user_id", user.id)
      .single();

    if (profile) {
      setWorkerId(profile.id);
      setCompanyId(profile.company_id);
      setSiteId(profile.site_id);
    }
  };

  const handleMicClick = async () => {
    if (isRecording) {
      // Stop recording
      if (audioRecorderRef.current) {
        const audioBlob = await audioRecorderRef.current.stop();
        setIsRecording(false);
        setIsTranscribing(true);

        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64Audio = (reader.result as string).split(",")[1];
          
          try {
            const { data, error } = await supabase.functions.invoke("transcribe-audio", {
              body: { audio: base64Audio },
            });

            if (error) throw error;
            if (data?.text) {
              setTranscript(prev => prev + (prev ? " " : "") + data.text);
              
              // Extract context from transcript
              if (companyId) {
                const { data: extracted } = await supabase.functions.invoke("extract-incident-context", {
                  body: {
                    description: data.text,
                    company_id: companyId,
                    site_id: siteId,
                  },
                });
                if (extracted) {
                  setExtractedContext(extracted);
                  // Auto-select severity if inferred
                  if (extracted.inferred_severity && !severity) {
                    const severityMap: Record<string, Severity> = {
                      "first_aid": "first_aid",
                      "medical_treatment": "medical_treatment",
                      "lost_time": "medical_treatment",
                      "fatality": "fatality",
                    };
                    const inferredSeverity = severityMap[extracted.inferred_severity];
                    if (inferredSeverity) setSeverity(inferredSeverity);
                  }
                }
              }
            }
          } catch (err) {
            console.error("Transcription error:", err);
            toast({
              title: "Gagal transkripsi",
              description: "Tidak dapat memproses rekaman suara",
              variant: "destructive",
            });
          } finally {
            setIsTranscribing(false);
          }
        };
        reader.readAsDataURL(audioBlob);
      }
    } else {
      // Start recording
      audioRecorderRef.current = new AudioRecorder();
      await audioRecorderRef.current.start();
      setIsRecording(true);
    }
  };

  const handlePhotoCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !workerId) return;

    const fileExt = file.name.split(".").pop();
    const fileName = `${Date.now()}-quick-incident.${fileExt}`;
    const filePath = `incidents/${workerId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from("peer-reports")
      .upload(filePath, file);

    if (uploadError) {
      toast({
        title: "Gagal upload foto",
        variant: "destructive",
      });
      return;
    }

    const { data: { publicUrl } } = supabase.storage
      .from("peer-reports")
      .getPublicUrl(filePath);

    setPhotoUrl(publicUrl);
  };

  const handleSubmit = async () => {
    if (!workerId || !companyId || !severity || !areaStatus) {
      toast({
        title: "Lengkapi data",
        description: "Pilih tingkat keparahan dan status area",
        variant: "destructive",
      });
      return;
    }

    if (!transcript.trim()) {
      toast({
        title: "Deskripsi kosong",
        description: "Rekam suara untuk menjelaskan kejadian",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Extract people_involved IDs if available
      const peopleInvolved = extractedContext?.people_involved
        ?.filter((p: any) => p.worker_id)
        ?.map((p: any) => p.worker_id) || [];

      const { data, error } = await supabase.functions.invoke("create-incident", {
        body: {
          reported_by: workerId,
          company_id: companyId,
          site_id: siteId,
          location: extractedContext?.location || "Lokasi tidak ditentukan",
          description: transcript,
          incident_datetime: new Date().toISOString(),
          injury_severity: severity,
          body_part: extractedContext?.body_part,
          mechanism_of_injury: extractedContext?.mechanism_of_injury,
          area_safety_status: areaStatus,
          photo_urls: photoUrl ? [photoUrl] : undefined,
          voice_transcript: transcript,
          people_involved: peopleInvolved.length > 0 ? peopleInvolved : undefined,
          hazard_category: extractedContext?.hazard_category,
          report_source: "quick",
        },
      });

      if (error) throw error;

      toast({
        title: "Insiden dilaporkan",
        description: areaStatus === "still_unsafe" 
          ? "⚠️ HSE dan Supervisor telah diberitahu!"
          : "Terima kasih atas laporannya",
      });

      navigate("/home");
    } catch (err) {
      console.error("Submit error:", err);
      toast({
        title: "Gagal melaporkan",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit = severity && areaStatus && transcript.trim().length > 5;

  return (
    <div className="min-h-screen bg-destructive/5">
      {/* Header */}
      <div className="bg-destructive text-destructive-foreground px-4 py-4 flex items-center gap-3 sticky top-0 z-10 pt-safe">
        <button onClick={() => navigate(-1)}>
          <ArrowLeft className="h-6 w-6" />
        </button>
        <div className="flex-1">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <Siren className="h-5 w-5" />
            LAPOR INSIDEN DARURAT
          </h1>
        </div>
        <AlertTriangle className="h-6 w-6 animate-pulse" />
      </div>

      <div className="p-4 max-w-lg mx-auto space-y-6 pb-32">
        {/* Voice Recording - Primary */}
        <Card className="border-2 border-destructive/30">
          <CardContent className="p-6">
            <motion.button
              onClick={handleMicClick}
              disabled={isTranscribing}
              className={cn(
                "w-full py-8 rounded-2xl flex flex-col items-center gap-4 transition-all",
                isRecording 
                  ? "bg-destructive text-destructive-foreground animate-pulse" 
                  : "bg-primary/10 text-primary hover:bg-primary/20"
              )}
              whileTap={{ scale: 0.98 }}
            >
              {isTranscribing ? (
                <Loader2 className="h-12 w-12 animate-spin" />
              ) : isRecording ? (
                <MicOff className="h-12 w-12" />
              ) : (
                <Mic className="h-12 w-12" />
              )}
              <span className="text-lg font-medium">
                {isTranscribing 
                  ? "Memproses..." 
                  : isRecording 
                    ? "TEKAN UNTUK BERHENTI" 
                    : "TEKAN UNTUK REKAM"}
              </span>
              <span className="text-sm opacity-70">
                Ceritakan apa yang terjadi
              </span>
            </motion.button>

            {transcript && (
              <div className="mt-4 p-3 bg-muted rounded-lg">
                <p className="text-sm">{transcript}</p>
              </div>
            )}

            {/* Extracted Context Preview */}
            {extractedContext && (
              <div className="mt-4 space-y-2">
                {extractedContext.people_involved?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {extractedContext.people_involved.map((p: any, i: number) => (
                      <Badge 
                        key={i} 
                        variant={p.worker_id ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {p.name} {p.worker_id && "✓"}
                      </Badge>
                    ))}
                  </div>
                )}
                {extractedContext.mechanism_of_injury && (
                  <Badge variant="outline" className="text-xs">
                    {extractedContext.mechanism_of_injury}
                  </Badge>
                )}
                {extractedContext.linked_signals?.length > 0 && (
                  <div className="p-2 bg-warning/10 rounded text-xs text-warning">
                    ⚠️ {extractedContext.linked_signals.length} sinyal terkait terdeteksi
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Severity Selection */}
        <div className="space-y-3">
          <h3 className="font-semibold text-center">Seberapa Parah?</h3>
          <div className="grid grid-cols-3 gap-3">
            {SEVERITY_OPTIONS.map((option) => (
              <motion.button
                key={option.value}
                onClick={() => setSeverity(option.value)}
                className={cn(
                  "p-4 rounded-xl border-2 flex flex-col items-center gap-2 transition-all",
                  severity === option.value 
                    ? `${option.color} border-current shadow-lg` 
                    : "bg-card border-border hover:border-primary/50"
                )}
                whileTap={{ scale: 0.95 }}
              >
                <span className="text-3xl">{option.icon}</span>
                <span className="text-xs font-medium text-center">{option.label}</span>
                {severity === option.value && (
                  <Check className="h-4 w-4" />
                )}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Area Status */}
        <div className="space-y-3">
          <h3 className="font-semibold text-center">Area Saat Ini:</h3>
          <div className="grid grid-cols-2 gap-3">
            {AREA_OPTIONS.map((option) => (
              <motion.button
                key={option.value}
                onClick={() => setAreaStatus(option.value)}
                className={cn(
                  "p-5 rounded-xl border-2 flex flex-col items-center gap-2 transition-all",
                  areaStatus === option.value 
                    ? `${option.color} border-current shadow-lg` 
                    : "bg-card border-border hover:border-primary/50"
                )}
                whileTap={{ scale: 0.95 }}
              >
                <span className="text-3xl">{option.icon}</span>
                <span className="font-bold">{option.label}</span>
                {areaStatus === option.value && (
                  <Check className="h-4 w-4" />
                )}
              </motion.button>
            ))}
          </div>
        </div>

        {/* Photo (Optional) */}
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => fileInputRef.current?.click()}
          >
            <Camera className="h-4 w-4 mr-2" />
            {photoUrl ? "Ganti Foto" : "Ambil Foto (Opsional)"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handlePhotoCapture}
          />
          {photoUrl && (
            <img
              src={photoUrl}
              alt="Evidence"
              className="w-16 h-16 object-cover rounded-lg border"
            />
          )}
        </div>

        {/* Link to full form */}
        <button
          onClick={() => navigate("/report-incident")}
          className="text-sm text-muted-foreground hover:text-primary transition text-center w-full py-2"
        >
          ↩ Lapor dengan form lengkap
        </button>
      </div>

      {/* Fixed Submit Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur border-t">
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit || isSubmitting}
          className={cn(
            "w-full h-14 text-lg font-bold",
            areaStatus === "still_unsafe" 
              ? "bg-destructive hover:bg-destructive/90" 
              : "bg-primary hover:bg-primary/90"
          )}
        >
          {isSubmitting ? (
            <Loader2 className="h-6 w-6 animate-spin" />
          ) : (
            <>
              <Siren className="h-6 w-6 mr-2" />
              KIRIM LAPORAN
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default QuickIncident;